import React from 'react';
import { ColDef } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { StockSymbol } from '@benzinga/session';
import { Crossing, PriceAlerts, PriceAlertsManager } from '@benzinga/price-alert-manager';
import { SessionContext } from '@benzinga/session-context';
import styled, { css } from '@benzinga/themetron';
import { PriceAlertDialog } from '../../PriceAlertDialog';
import Hooks from '@benzinga/hooks';

interface NoteCellData {
  symbol: StockSymbol;
}

interface OwnProps {
  data: NoteCellData;
}
type Props = OwnProps & CustomCellRendererProps;

// Pric Alert Tooltip Cell Renderer
const PriceAlertCellRenderer: React.FC<Props> = React.memo(params => {
  const [priceAlert, setPriceAlert] = React.useState<PriceAlerts.IPriceAlert | null>();
  const session = React.useContext(SessionContext);
  const priceAlertManager = React.useMemo(() => session.getManager(PriceAlertsManager), [session]);
  const isUpPrice = priceAlert?.crossing === Crossing.UP;

  const updatePriceAlert = React.useCallback(
    (alerts: Partial<PriceAlerts.Alert>[]) => {
      const result = alerts.filter(alert => alert.symbol === params.data.symbol);
      setPriceAlert(result[result.length - 1]?.priceAlert);
    },
    [params.data.symbol],
  );

  const loadPriceAlerts = React.useCallback(async () => {
    const storedAlerts = priceAlertManager.getStoredPriceAlerts();
    if (storedAlerts?.length) {
      updatePriceAlert(storedAlerts);
    } else {
      // Only fetch if no stored data
      priceAlertManager.getPriceAlerts().then(result => {
        if (result.ok && result.ok.length > 0) {
          updatePriceAlert(result.ok);
        }
      });
    }
  }, [priceAlertManager, updatePriceAlert]);

  React.useEffect(() => {
    loadPriceAlerts();
  }, [loadPriceAlerts]);

  Hooks.useSubscriber(priceAlertManager, event => {
    if (event.type === 'price_alerts_updated') {
      updatePriceAlert(event.categoriesAlerts);
    }
  });

  const onEditClicked = React.useCallback(() => {
    if (params.node.rowIndex !== null) {
      params.api?.startEditingCell({
        colKey: params.column?.getColId() ?? '',
        rowIndex: params.node.rowIndex,
        ...params,
      });
    }
  }, [params]);

  return (
    <Container>
      <Text state={isUpPrice ? 'positive' : 'negative'}>
        {priceAlert?.price && priceAlert.price > 0 ? (
          <>
            <DirectionalArrowIcon state={isUpPrice ? 'positive' : 'negative'} />
            {priceAlert.price}
          </>
        ) : (
          '' // Render nothing if no alert exists
        )}
      </Text>
      <Edit onClick={onEditClicked}>
        <EditIcon />
      </Edit>
    </Container>
  );
});
PriceAlertCellRenderer.displayName = 'PriceAlertCellRenderer';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
`;

const Text = styled.div<{ state: 'positive' | 'negative' | 'neutral' }>`
  color: ${props => props.theme.colors.statistic[props.state]} !important;
  fill: ${props => props.theme.colors.statistic[props.state]} !important;

  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Edit = styled.div`
  flex: 0 0 auto;
  cursor: pointer;
  display: flex;
  align-items: center;

  svg {
    vertical-align: middle;
    fill: ${props => props.theme.colors.foreground};
  }
`;

// Price Alert Tooltip
const PriceAlertCellEditor = React.memo((params: CustomCellRendererProps & { stopEditing: () => void }) => {
  const handleClose = React.useCallback(() => {
    if (params.api && params.node) {
      params.api.refreshCells({ force: true, rowNodes: [params.node] });
    }
    // This check is fine, ensures we don't call stopEditing if already stopped
    if (params.api.getEditingCells().length !== 0) {
      params.stopEditing();
    }
  }, [params]);

  return (
    <PriceAlertDialog
      allowEdit={params.api.getEditingCells().length !== 0}
      onClose={handleClose}
      symbol={params.data.symbol}
    />
  );
});

export const PriceAlertColDef = (colDef: ColDef): ColDef => ({
  cellClass: 'u-alignLeft',
  cellEditor: PriceAlertCellEditor,
  cellEditorPopup: true,
  cellRenderer: PriceAlertCellRenderer,
  editable: true,
  filter: false,
  sortable: false,
  tooltipComponent: PriceAlertCellEditor,
  tooltipField: colDef.field,
  ...colDef,
});

const ArrowSvg = styled.svg<{ state: 'positive' | 'negative' }>`
  margin-right: 3px;
  font-size: 11px;
  ${props =>
    props.state === 'negative' &&
    css`
      transform: rotate(180deg);
    `}
`;

const DirectionalArrowIcon: React.FC<{
  state: 'positive' | 'negative';
}> = ({ state }) => (
  <ArrowSvg height="1em" state={state} viewBox="0 0 24 24" width="1em">
    <path
      d="M0.0125411251,2.00838294 C-0.153916274,2.21272374 1.53964426,5.50356978 5.09322274,11.8809211 C1.53964426,18.2594203 -0.154490265,21.5502663 0.010819152,21.7534592 C0.122173412,21.892365 8.09433485,18.601519 23.9273035,11.8809211 C8.14943799,5.09488817 0.177850542,1.80404213 0.0125411251,2.00838294 Z"
      transform="translate(11.963550, 11.878551) rotate(-90.000000) translate(-11.963550, -11.878551) "
    />
  </ArrowSvg>
);

const EditIcon: React.FC = () => (
  <svg
    aria-labelledby="edit-title"
    height="1em"
    preserveAspectRatio="xMidYMid meet"
    viewBox="0 0 24 24"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title id="edit-title">edit</title>
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);
